/**
 * Checkout coupon — toggle "Mam kod rabatowy" + apply/remove via REST.
 *
 * Markup w partials/checkout/order-review.php:
 *   [data-hn-co-coupon-wrap]          — root wrapper, toggle `.is-open` na nim
 *   [data-hn-co-coupon-trigger]       — przycisk "Mam kod rabatowy"
 *   [data-hn-co-coupon-collapse]      — grid-rows animowany collapse
 *   [data-hn-co-coupon-form]          — form z inputem `coupon_code`
 *   [data-hn-co-coupon-msg]           — komunikat sukces/error (aria-live)
 *   [data-hn-co-coupon-remove="CODE"] — przycisk × przy zastosowanym kodzie
 *
 * REST endpoint (PHP: inc/cart-rewards.php → /hn/v1/side-cart/coupon):
 *   POST { code, action_type: 'apply' | 'remove' }
 *   → { html, count, total, message: {type, text} }
 *
 * Po sukcesie triggerujemy jQuery event `update_checkout` — WC core re-renderuje
 * #order_review (totals + payment) AJAX-em, podchwytując zaktualizowany cart.
 */

function getCouponEndpoint() {
    // Buduj z HN_SIDECART.urlGet, podmieniając `/side-cart` na `/side-cart/coupon`.
    const c = window.HN_SIDECART;
    if (!c?.urlGet) return null;
    return c.urlGet.replace(/\/side-cart\/?$/, '/side-cart/coupon');
}

async function postCoupon(code, actionType) {
    const url = getCouponEndpoint();
    if (!url || !window.HN_SIDECART?.nonce) return null;
    try {
        const fd = new FormData();
        fd.append('code', code);
        fd.append('action_type', actionType);
        const res = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': window.HN_SIDECART.nonce, 'Accept': 'application/json' },
            body: fd,
        });
        if (!res.ok) return { message: { type: 'error', text: 'Network error' } };
        return await res.json();
    } catch (_) {
        return { message: { type: 'error', text: 'Network error' } };
    }
}

function showMsg(form, type, text) {
    const msgEl = form?.parentElement?.querySelector('[data-hn-co-coupon-msg]');
    if (!msgEl) return;
    msgEl.textContent = text || '';
    msgEl.dataset.type = type || '';
}

function bindToggles() {
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-hn-co-coupon-trigger]');
        if (!trigger) return;
        e.preventDefault();
        const wrap = trigger.closest('[data-hn-co-coupon-wrap]');
        if (!wrap) return;
        const isOpen = wrap.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) {
            const input = wrap.querySelector('.hn-co__coupon-input');
            if (input) setTimeout(() => input.focus(), 250);
        }
    });
}

async function doApply(wrap) {
    if (!wrap) return;
    const input = wrap.querySelector('input[name="coupon_code"], [data-hn-co-coupon-input]');
    const btn   = wrap.querySelector('[data-hn-co-coupon-apply], button[type="submit"]');
    const code  = (input?.value || '').trim();
    if (!code) {
        showMsg(wrap, 'error', 'Wpisz kod rabatowy');
        return;
    }

    if (btn) { btn.disabled = true; btn.dataset.origText = btn.textContent; btn.textContent = '…'; }
    showMsg(wrap, '', '');

    const data = await postCoupon(code, 'apply');

    if (btn) { btn.disabled = false; btn.textContent = btn.dataset.origText || 'Zastosuj'; }

    if (!data || data.message?.type === 'error') {
        showMsg(wrap, 'error', data?.message?.text || 'Nie udało się zastosować kodu.');
        return;
    }

    showMsg(wrap, 'success', data.message?.text || 'Kod zastosowany!');
    if (input) input.value = '';

    applyCouponUpdate(data);
}

function bindSubmit() {
    // Click on apply button. Markup używa `<div data-hn-co-coupon-form>` zamiast `<form>`
    // bo nested form'y w HTML5 są ignorowane (cała sekcja jest w `<form class="checkout">`).
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-hn-co-coupon-apply]');
        if (!btn) return;
        e.preventDefault();
        const wrap = btn.closest('[data-hn-co-coupon-form]');
        doApply(wrap);
    });
    // Enter w input'cie kuponu — equivalent do submit form'a.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        const input = e.target.closest('[data-hn-co-coupon-form] input[name="coupon_code"], [data-hn-co-coupon-input]');
        if (!input) return;
        e.preventDefault();
        const wrap = input.closest('[data-hn-co-coupon-form]');
        doApply(wrap);
    });
    // Backward compat — gdyby gdzieś (side-cart) faktyczny `<form>` jeszcze działał.
    document.addEventListener('submit', (e) => {
        const wrap = e.target.closest('form[data-hn-co-coupon-form]');
        if (!wrap) return;
        e.preventDefault();
        doApply(wrap);
    });
}

/**
 * Po remove kuponu trzeba zaktualizować widok wszędzie gdzie kupon był pokazany:
 *  1. Side-cart body — swap całego `[data-hn-sidecart-body]` jeśli response ma `html`
 *  2. Header mini-cart count + total
 *  3. Checkout `#order_review` — przez WC `update_checkout` event
 *  4. Cart page `/koszyk/` — full reload (najprostsze, totals + items kompleksowo)
 */
async function applyCouponUpdate(data) {
    if (!data) return;

    // 1. Side-cart body swap (response z /side-cart/coupon zwraca `html` z side-cart-body partial).
    if (data.html) {
        const body = document.querySelector('[data-hn-sidecart-body]');
        if (body) {
            const prevScroll = body.scrollTop;
            body.innerHTML = data.html;
            requestAnimationFrame(() => {
                const max = Math.max(0, body.scrollHeight - body.clientHeight);
                body.scrollTop = Math.min(prevScroll, max);
            });
        }
    }

    // 2. Header mini-cart count + total.
    if ('count' in data) {
        document.querySelectorAll('[data-hn-header-cart-count], [data-hn-sidecart-count]').forEach((el) => {
            el.textContent = String(data.count);
            el.classList.toggle('is-empty', data.count <= 0);
        });
    }
    if ('total' in data && data.total) {
        document.querySelectorAll('[data-hn-header-cart-total]').forEach((el) => {
            el.innerHTML = data.total;
        });
    }

    // 3. Cart page (/koszyk/) — full reload bo coupon influences totals AND items.
    if (document.body.classList.contains('woocommerce-cart')) {
        window.location.reload();
        return;
    }

    // 4. Checkout — direct refresh order-review przez nasz `/refresh-shipping`
    //    endpoint który zwraca świeży `review` HTML z aktualnym stanem koszyka
    //    (po coupon mutate). Nie polegamy na WC `update_checkout` event'cie
    //    (nasz custom checkout layout bywa nie-zbindowany na czas — race condition).
    const onCheckout = document.querySelector('form.woocommerce-checkout, form.checkout.hn-co');
    const co = window.HN_CHECKOUT;
    if (onCheckout && co && co.restShipping) {
        try {
            const ship = document.getElementById('ship_to_different_address');
            const prefix = (ship && ship.checked) ? 'shipping' : 'billing';
            const get = (name) => {
                const el = document.querySelector(`[name="${name}"]`);
                return el ? el.value.trim() : '';
            };
            const fd = new FormData();
            fd.append('country',  get(`${prefix}_country`) || 'PL');
            fd.append('postcode', get(`${prefix}_postcode`));
            fd.append('city',     get(`${prefix}_city`));
            const res = await fetch(co.restShipping, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': co.nonce, 'Accept': 'application/json' },
                body: fd,
            });
            if (res.ok) {
                const refresh = await res.json();
                const review = document.getElementById('order_review');
                if (review && refresh.review) {
                    review.innerHTML = refresh.review;
                }
                if (refresh.fragments && refresh.fragments.total) {
                    document.querySelectorAll('[data-hn-co-submit-total], [data-hn-co-mobile-total], [data-hn-co-grand-total]').forEach((el) => {
                        el.innerHTML = refresh.fragments.total;
                    });
                }
                // Swap section-shipping — niektóre kupony zmieniają dostępne metody
                // dostawy (np. coupon typu "free_shipping" dodaje rate `free_shipping`,
                // restricted coupon może wyłączyć carrier). Bez tego swap'a UI pokazywałby
                // stare rates dopóki user nie zmieni adresu.
                const shipSection = document.querySelector('[data-hn-co-section="shipping"]');
                if (shipSection && refresh.shipping) {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = refresh.shipping;
                    const fresh = tmp.querySelector('[data-hn-co-section="shipping"]');
                    if (fresh) {
                        shipSection.replaceWith(fresh);
                        // Re-trigger update_checkout — InPost / Furgonetka pluginy
                        // nasłuchują tego eventu żeby re-init swoich widgetów po DOM swap.
                        if (window.jQuery) {
                            window.jQuery(document.body).trigger('updated_shipping_method');
                        }
                    }
                }
                // Swap section-payment — kupon free_shipping może zmienić chosen
                // shipping, co cascade'uje do dostępnych payments (np. COD shipping
                // usunięty z list bo free shipping → COD payment też powinno zniknąć).
                const paymentSection = document.querySelector('[data-hn-co-section="payment"]');
                if (paymentSection && refresh.payment) {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = refresh.payment;
                    const fresh = tmp.querySelector('[data-hn-co-section="payment"]');
                    if (fresh) paymentSection.replaceWith(fresh);
                }
            }
        } catch (_) {
            // Fallback: trigger jQuery update_checkout — może WC core to obsłuży.
            if (window.jQuery) window.jQuery(document.body).trigger('update_checkout');
        }
    } else if (window.jQuery) {
        // Nie na checkoucie — niech WC sam zdecyduje co odświeżyć.
        window.jQuery(document.body).trigger('update_checkout');
    }

    document.dispatchEvent(new CustomEvent('hn:cart:updated', { detail: data }));
}

function bindRemove() {
    document.addEventListener('click', async (e) => {
        // Obsługujemy oba warianty atrybutu:
        //   - `[data-hn-co-coupon-remove]` — checkout (partials/checkout/order-review.php)
        //   - `[data-hn-coupon-remove]`    — side-cart (partials/side-cart-body.php)
        const btn = e.target.closest('[data-hn-co-coupon-remove], [data-hn-coupon-remove]');
        if (!btn) return;
        e.preventDefault();
        const code = btn.dataset.hnCoCouponRemove || btn.dataset.hnCouponRemove;
        if (!code) return;

        btn.disabled = true;
        const data = await postCoupon(code, 'remove');
        btn.disabled = false;

        applyCouponUpdate(data);
    });
}

export function initCheckoutCoupon() {
    // Brak bail-outu na puste DOM — handlery są delegowane na document, więc działają
    // też dla elementów dodanych później (np. side-cart panel renderowany lazy AJAX'em,
    // order-review swap po update_checkout, dynamiczne dodanie kodu rabatowego).
    if (!window.HN_SIDECART || !window.HN_SIDECART.nonce) return;
    bindToggles();
    bindSubmit();
    bindRemove();
}
